import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseBufferedFieldResult<T> {
  localValue: T;
  setLocalValue: React.Dispatch<React.SetStateAction<T>>;
  handleFocus: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleBlur: () => void;
}

// Local-buffer pattern for inputs: hold the in-flight value in component state
// and only push to the external store (Formik) on blur. The focus ref keeps
// external value updates from clobbering an in-progress edit.
export function useBufferedField<T>(
  externalValue: T,
  commit: (value: T) => void,
  onCommittedBlur?: (value: T) => void,
  externalOnFocus?: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >
): UseBufferedFieldResult<T> {
  const [localValue, setLocalValue] = useState<T>(externalValue);
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) {
      setLocalValue(externalValue);
    }
  }, [externalValue]);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      isFocused.current = true;
      externalOnFocus?.(e);
    },
    [externalOnFocus]
  );

  const handleBlur = useCallback(() => {
    isFocused.current = false;
    if (localValue !== externalValue) {
      commit(localValue);
    }
    onCommittedBlur?.(localValue);
  }, [localValue, externalValue, commit, onCommittedBlur]);

  return { localValue, setLocalValue, handleFocus, handleBlur };
}
